/**
 * Homepage hero video — a Vimeo background loop with custom controls that
 * pauses when scrolled out of view. Ported from vimeo-scroll-test-v2.html;
 * see that file for the annotated rationale behind each decision below.
 *
 * One deliberate change from the demo: state is updated optimistically
 * (synchronously, in the same handler that issues the play/pause command)
 * rather than solely via `player.on('play'/'pause', ...)`. Those events did
 * not reliably fire for API-triggered play/pause in testing — confirmed by
 * reproducing the same non-update in the demo page itself — so relying on
 * them alone left the button's icon/label out of sync with the actual
 * player state. The listeners stay too, as a harmless defensive fallback.
 */
export function initVideoHero() {
  const section  = document.querySelector('[data-video-hero]');
  if (!section) return;

  const iframe    = section.querySelector('[data-video-iframe]');
  const hitTarget = section.querySelector('[data-video-reveal]');
  const toggleBtn = section.querySelector('[data-video-toggle]');
  const restartBtn = section.querySelector('[data-video-restart]');
  const soundBtn  = section.querySelector('[data-video-sound]');

  if (!iframe) return;

  // Fallback reveal for the no-SDK path below (window.Vimeo unavailable) —
  // the native `load` event is the only signal available there.
  iframe.addEventListener('load', () => section.classList.add('is-video-ready'), { once: true });

  if (!window.Vimeo) return; // degrades to the plain looping embed

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const player = new Vimeo.Player(iframe);

  // Primary reveal signal: ready() is a postMessage handshake with the
  // player, so — unlike the native `load` listener above — it still
  // resolves correctly even if the iframe's own document already finished
  // loading before this script ran (e.g. a cached embed loading fast enough
  // to win the race against a deferred bundle). Without this, that race
  // strands the video invisible behind the poster indefinitely.
  player.ready()
    .then(() => section.classList.add('is-video-ready'))
    .catch(() => section.classList.add('is-video-ready')); // fail open

  // Local mirrors of player state. Keeping these in sync means the click
  // handlers can act immediately instead of awaiting a getter first — which
  // is what makes unmuting work. Browsers only permit it inside a live user
  // gesture, and an await lets that gesture expire.
  let isMuted    = true;
  let isPaused   = reduceMotion;
  let userPaused = reduceMotion;

  function draw() {
    toggleBtn.classList.toggle('is-paused', isPaused);
    toggleBtn.setAttribute('aria-label', isPaused ? 'Play video' : 'Pause video');

    soundBtn.classList.toggle('is-muted', isMuted);
    soundBtn.setAttribute('aria-label', isMuted ? 'Unmute video' : 'Mute video');
  }
  draw();

  if (reduceMotion) {
    player.pause().catch(() => {});
  }

  // --- pause when scrolled out of view -----------------------
  new IntersectionObserver(entries => {
    const inView = entries[0].isIntersecting;
    if (inView) {
      if (!userPaused) {
        isPaused = false;
        draw();
        player.play().catch(() => {});
      }
    } else {
      isPaused = true;
      draw();
      player.pause().catch(() => {});
    }
  }, { threshold: 0.25 }).observe(iframe);

  // --- reveal controls on tap/click -----------------------------
  // A cross-origin iframe swallows pointer events within its own bounds, so
  // a click on the video itself never reaches a listener on an ancestor —
  // this invisible overlay is what "select the video" actually binds to.
  // :hover/:focus-within in CSS cover mouse-hover and keyboard tab already.
  if (hitTarget) {
    hitTarget.addEventListener('click', () => {
      section.classList.add('is-active');
    });
  }

  // --- play / pause ------------------------------------------
  toggleBtn.addEventListener('click', () => {
    if (isPaused) {
      userPaused = false;
      isPaused   = false;
      draw();
      player.play().catch(() => {});
    } else {
      userPaused = true;
      isPaused   = true;
      draw();
      player.pause().catch(() => {});
    }
  });

  // --- mute / unmute -------------------------------------------
  // isMuted is the single source of truth. Vimeo tracks `muted` and
  // `volume` separately, and unmuting fires volumechange while the volume
  // is still 0 — so inferring mute state from that event fights this
  // handler and the icon drifts out of step with the audio. No await
  // either: unmuting only works inside a live user gesture, and awaiting a
  // getter lets that gesture expire.
  soundBtn.addEventListener('click', () => {
    isMuted = !isMuted;
    if (isMuted) {
      player.setMuted(true).catch(() => {});
    } else {
      player.setMuted(false).catch(() => {});
      player.setVolume(1).catch(() => {});
    }
    draw();
  });

  // --- restart -------------------------------------------------
  // Clears a deliberate pause too: asking for a restart is asking for it
  // to play.
  restartBtn.addEventListener('click', () => {
    userPaused = false;
    isPaused   = false;
    draw();
    player.setCurrentTime(0).then(() => player.play()).catch(() => {});
  });

  // --- keep mirrors honest (defensive fallback) -------------------
  // Redundant with the optimistic updates above whenever the events do
  // fire; harmless when they don't.
  player.on('play',  () => { isPaused = false; draw(); });
  player.on('pause', () => { isPaused = true;  draw(); });
}
